  (() => {
    
    class BundleBuilder {
      constructor(element) {
        this.element = element;
        this.sectionId = element.dataset.sectionId;
        
        // Get from config or data attributes with fallbacks
        const config = window.BundleBuilderConfig || {};
        this.bundleTagKey = config.bundleTagKey || element.querySelector('[data-bundle-tag-key]')?.dataset.bundleTagKey || 'bundle';
        this.bundleTagValue = config.bundleTagValue || element.querySelector('[data-bundle-tag-value]')?.dataset.bundleTagValue || 'TESTBUNDLE15';
        
        // Bundle requirements from section settings (parse from metaobject strings)
        this.minimumProducts = parseInt(config.minimumProducts) || 2;
        this.maximumProducts = parseInt(config.maximumProducts) || 3;
        this.allowPartialBundles = config.allowPartialBundles === 'True' || config.allowPartialBundles === true;
        
        // Discount messaging settings from section settings (parse from metaobject strings)
        this.discountSettings = {
          showMessaging: config.showDiscountMessaging === true, // Site-wide checkbox setting
          percentage: parseInt(config.discountPercentage) || 15, // From metaobject
          qualifiedMessage: config.discountQualifiedMessage || 'Save 15% on your bundle',
          progressMessage: config.discountProgressMessage || 'Add {count} more to save 15%'
        };
        
        // Translation strings
        this.translations = config.translations || {};
        
        // Set size option name after translations are loaded
        this.sizeOptionName = element.dataset.sizeOptionName || (this.translations.labels?.size || 'Size');
        
        this.state = {
          selectedProducts: new Map(),
          currentSize: null,
          isLoading: false,
          variantCache: new Map()
        };
        
        this.animationTimeouts = [];
        
        // Cache commonly used DOM queries
        this.cachedElements = {
          allProducts: null,
          lastCacheTime: 0,
          cacheTimeout: 1000 // Cache for 1 second
        };
        
        // Animation timing constants
        this.ANIMATION_DELAYS = {
          STAGGER_STEP: 40, // ms between each item animation
          FADE_DURATION: 250, // ms for fade transitions
          CART_OPEN_DELAY: 100, // ms delay before opening cart
          INITIAL_DELAY: 50, // ms initial delay for animations
          LOADING_DELAY: 5000 // ms for loading state timeout
        };
        
        this.elements = {
          groupTabs: element.querySelectorAll('input[name^="bundle-group"]'),
          groups: element.querySelectorAll('.bundle-builder__group'),
          productSelection: element.querySelector('[data-product-selection]'),
          productSizes: element.querySelector('[data-product-sizes]'),
          selectItemBtn: element.querySelector('[data-select-item]'),
          addToBundleBtn: element.querySelector('[data-add-to-bundle]'),
          selectionCards: element.querySelectorAll('.bundle-builder__selection-card'),
          addToCartBtn: element.querySelector('.bundle-builder__add-to-cart'),
          mobileAddToCartBtn: element.querySelector('.bundle-builder__mobile-add-to-cart'),
          mobileSummary: element.querySelector('[data-mobile-summary]'),
          bundleCount: element.querySelector('[data-bundle-count]'),
          bundleTotal: element.querySelectorAll('.bundle-builder__total-price'),
          progressPricing: element.querySelector('.bundle-builder__progress-pricing'),
          countCurrent: element.querySelector('.bundle-builder__count-current'),
          countTotal: element.querySelector('.bundle-builder__count-total'),
          selectedProducts: element.querySelector('[data-selected-products]'),
          originalTotal: element.querySelector('[data-original-total]'),
          finalTotal: element.querySelector('[data-final-total]'),
          mobileCount: element.querySelector('[data-mobile-count]'),
          mobileTotal: element.querySelector('[data-mobile-total]'),
          savingsMessage: element.querySelectorAll('[data-savings-message]'),
          errorMessage: element.querySelector('[data-error-message]'),
          selectionAnnouncement: element.querySelector('[data-selection-announcement]'),
          loadingAnnouncement: element.querySelector('[data-loading-announcement]')
        };
        
        this.init();
      }
      
      
      async init() {
        this.setupEventListeners();
        this.setupKeyboardNavigation();
        this.applyInitialGenderFilter();
        this.preloadHiddenImages();
        this.updateUI();
        
        // Initialize carousel if in carousel mode on mobile
        const mobileLayout = this.element.dataset.mobileLayout || 'grid';
        if (mobileLayout === 'carousel' && window.innerWidth <= 999) {
          this.initCarousel();
        }
        
        this.dispatchEvent('bundle:initialized', {
          sectionId: this.sectionId,
          groups: this.getGroupCount()
        });
      }
      
      // Helper method for cached DOM queries
      getCachedProducts(group = null) {
        const now = Date.now();
        if (this.cachedElements.allProducts && (now - this.cachedElements.lastCacheTime) < this.cachedElements.cacheTimeout) {
          return group ? this.cachedElements.allProducts.filter(p => p.closest(`[data-group-id="${group}"]`)) : this.cachedElements.allProducts;
        }
        
        // Refresh cache
        this.cachedElements.allProducts = Array.from(this.element.querySelectorAll('button.bundle-product-item'));
        this.cachedElements.lastCacheTime = now;
        
        return group ? this.cachedElements.allProducts.filter(p => p.closest(`[data-group-id="${group}"]`)) : this.cachedElements.allProducts;
      }
      
      
      applyInitialGenderFilter() {
        // Apply the initially selected gender filter
        const selectedGenderInput = this.element.querySelector('input[name^="bundle-gender"]:checked');
        if (selectedGenderInput) {
          const selectedGender = selectedGenderInput.value;
          
          // Filter products without animation on initial load
          this.elements.groups.forEach(group => {
            const products = group.querySelectorAll('.bundle-product-item');
            
            products.forEach(product => {
              const productGender = product.dataset.productGender;
              
              if (productGender === selectedGender || productGender === 'unisex') {
                // Show product but respect pagination limits
                product.classList.remove('bundle-product-item--hidden-gender');
              } else {
                // Hide product that doesn't match gender
                product.classList.add('bundle-product-item--hidden-gender');
              }
            });
          });
        }
      }
      
      setupEventListeners() {
        // Use event delegation for dynamic content
        this.boundClickHandler = (e) => {
          const productItem = e.target.closest('button.bundle-product-item');
          if (productItem && !productItem.classList.contains('bundle-product-item--disabled')) {
            this.handleProductClick(productItem);
            return;
          }
          
          const removeBtn = e.target.closest('.bundle-builder__product-remove');
          if (removeBtn) {
            this.handleProductRemoval(removeBtn.dataset.selectionKey);
            return;
          }
          
          // Legacy selection removal
          const legacyRemoveBtn = e.target.closest('.bundle-builder__selection-remove');
          if (legacyRemoveBtn) {
            const selectionCard = legacyRemoveBtn.closest('.bundle-builder__selection-card');
            this.handleLegacySelectionRemoval(selectionCard);
            return;
          }
        };
        
        this.element.addEventListener('click', this.boundClickHandler);
        
        // Gender selector filtering (static elements)
        const genderInputs = this.element.querySelectorAll('input[name^="bundle-gender"]');
        genderInputs.forEach(input => {
          input.addEventListener('change', (e) => this.handleGenderFilter(e));
        });
        
        // Group tab switching (static elements)
        this.elements.groupTabs.forEach(tab => {
          tab.addEventListener('change', (e) => this.handleGroupTabChange(e));
        });
        
        // Size selection in product selection area (dynamic content)
        if (this.elements.productSizes) {
          this.elements.productSizes.addEventListener('change', (e) => this.handleSizeChange(e));
        }
        
        // Static buttons
        if (this.elements.selectItemBtn) {
          this.elements.selectItemBtn.addEventListener('click', (e) => this.handleSelectItem(e));
        }
        
        if (this.elements.addToBundleBtn) {
          this.elements.addToBundleBtn.addEventListener('click', (e) => this.handleAddToBundle(e));
        }
        
        if (this.elements.addToCartBtn) {
          this.elements.addToCartBtn.addEventListener('click', (e) => this.handleAddToCart(e));
        }
        
        if (this.elements.mobileAddToCartBtn) {
          this.elements.mobileAddToCartBtn.addEventListener('click', (e) => this.handleAddToCart(e));
        }
        
      }
      
      setupKeyboardNavigation() {
        // Add keyboard navigation for product grid
        this.element.addEventListener('keydown', (e) => {
          const focusedProduct = e.target.closest('button.bundle-product-item');
          if (!focusedProduct) return;
          
          const visibleProducts = Array.from(this.element.querySelectorAll('button.bundle-product-item:not(.bundle-product-item--hidden):not(.bundle-product-item--hidden-gender)'));
          const currentIndex = visibleProducts.indexOf(focusedProduct);
          
          if (currentIndex === -1) return;
          
          let targetIndex = -1;
          const columnsPerRow = 3; // Assuming 3-column grid
          
          switch (e.key) {
            case 'ArrowRight':
              targetIndex = currentIndex + 1;
              break;
            case 'ArrowLeft':
              targetIndex = currentIndex - 1;
              break;
            case 'ArrowDown':
              targetIndex = currentIndex + columnsPerRow;
              break;
            case 'ArrowUp':
              targetIndex = currentIndex - columnsPerRow;
              break;
            default:
              return; // Don't prevent default for other keys
          }
          
          // Check if target index is valid
          if (targetIndex >= 0 && targetIndex < visibleProducts.length) {
            e.preventDefault();
            visibleProducts[targetIndex].focus();
          }
        });
      }
      
      
      
      preloadHiddenImages() {
        const hiddenGroups = this.element.querySelectorAll('.bundle-builder__group--hidden');
        
        hiddenGroups.forEach(group => {
          const images = group.querySelectorAll('img[loading="lazy"]');
          images.forEach(img => {
            // Remove lazy loading to force browser cache
            img.loading = 'eager';
          });
        });
        
      }
      
      handleGenderFilter(event) {
        const selectedGender = event.target.value; // 'men' or 'women'
        
        // Sync all other gender radio buttons to match the selected one
        const allGenderInputs = this.element.querySelectorAll('input[name^="bundle-gender"]');
        allGenderInputs.forEach(input => {
          if (input.value === selectedGender && input !== event.target) {
            input.checked = true;
          } else if (input !== event.target) {
            input.checked = false;
          }
        });
        
        let totalProducts = 0;
        let shownProducts = 0;
        let hiddenProducts = 0;
        
        // Filter products in all groups based on selected gender
        this.elements.groups.forEach(group => {
          const products = group.querySelectorAll('button.bundle-product-item');
          
          products.forEach(product => {
            totalProducts++;
            const productGender = product.dataset.productGender;
            
            
            if (productGender === selectedGender || productGender === 'unisex') {
              // Show products that match the selected gender OR are unisex
              product.classList.remove('bundle-product-item--hidden-gender');
              shownProducts++;
              if (productGender === 'unisex') {
              } else {
              }
            } else {
              // Hide products that don't match
              product.classList.add('bundle-product-item--hidden-gender');
              hiddenProducts++;
            }
          });
        });
        
        
        // Clear any current product selection since products may have changed
        this.resetProductSelection();
        
        // Trigger a smooth re-layout animation for visible products
        this.animateGenderSwitch(selectedGender);
        
        // Update UI
        this.updateUI();
        
        // Reinitialize carousels after gender filter changes
        this.reinitializeMobileCarousels();
        
        // Dispatch gender change event
        this.dispatchEvent('bundle:gender_changed', {
          selectedGender: selectedGender,
          sectionId: this.sectionId
        });
      }
      
      animateGenderSwitch(selectedGender) {
        const activeGroup = this.element.querySelector('.bundle-builder__group:not(.bundle-builder__group--hidden)');
        if (!activeGroup) return;
        
        const visibleProducts = activeGroup.querySelectorAll('button.bundle-product-item:not(.bundle-product-item--hidden-gender):not(.bundle-product-item--hidden)');
        
        visibleProducts.forEach((product, index) => {
          product.classList.add('bundle-product-item--fade-in', 'bundle-product-item--animating');
          product.style.transitionDelay = `${index * this.ANIMATION_DELAYS.STAGGER_STEP}ms`;
          
          requestAnimationFrame(() => {
            product.classList.add('bundle-product-item--animate');
          });
          
          // Cleanup after animation
          setTimeout(() => {
            product.classList.remove('bundle-product-item--fade-in', 'bundle-product-item--animate', 'bundle-product-item--animating');
            product.style.transitionDelay = '';
          }, this.ANIMATION_DELAYS.FADE_DURATION + (index * this.ANIMATION_DELAYS.STAGGER_STEP));
        });
        
      }
      
      advanceToNextEmptyGroup() {
        const currentGroupId = this.getCurrentGroupId();
        const allGroupTabs = Array.from(this.elements.groupTabs);
        
        if (allGroupTabs.length === 0) return; // No tabs to switch between
        
        // Find current tab index
        const currentTabIndex = allGroupTabs.findIndex(tab => tab.dataset.groupId === currentGroupId);
        
        if (currentTabIndex === -1) return; // Current tab not found
        
        // Look for next empty group after current
        for (let i = currentTabIndex + 1; i < allGroupTabs.length; i++) {
          const tabGroupId = allGroupTabs[i].dataset.groupId;
          
          // Check if this group already has a selection
          const hasSelection = Array.from(this.state.selectedProducts.values())
            .some(selection => selection.groupId === tabGroupId);
          
          if (!hasSelection) {
            // Switch to this tab
            allGroupTabs[i].checked = true;
            allGroupTabs[i].dispatchEvent(new Event('change'));
            
            // Announce tab change to screen readers
            const groupLabel = this.getGroupLabel(tabGroupId);
            this.announceToScreenReader(`Switched to ${groupLabel} selection`);
            
            return; // Stop after switching
          }
        }
        
        // No empty groups after current, check from beginning
        for (let i = 0; i < currentTabIndex; i++) {
          const tabGroupId = allGroupTabs[i].dataset.groupId;
          
          const hasSelection = Array.from(this.state.selectedProducts.values())
            .some(selection => selection.groupId === tabGroupId);
          
          if (!hasSelection) {
            allGroupTabs[i].checked = true;
            allGroupTabs[i].dispatchEvent(new Event('change'));
            
            const groupLabel = this.getGroupLabel(tabGroupId);
            this.announceToScreenReader(`Switched to ${groupLabel} selection`);
            
            return;
          }
        }
        
        // All groups have selections - don't switch tabs
      }
      
      updateGroupTabStates() {
        // Clear all completed states first
        const allGroupOptions = this.element.querySelectorAll('.bundle-group-option');
        allGroupOptions.forEach(option => {
          option.classList.remove('bundle-group-option--completed');
        });
        
        // Mark groups that have selections
        this.state.selectedProducts.forEach((selection, selectionKey) => {
          const groupId = selection.groupId;
          const groupOption = this.element.querySelector(`input[data-group-id="${groupId}"]`)?.closest('.bundle-group-option');
          
          if (groupOption) {
            groupOption.classList.add('bundle-group-option--completed');
          }
        });
      }
      
      updateProductGridSelection() {
        // Clear all selected states first (but preserve focus states)
        const allProductItems = this.element.querySelectorAll('.bundle-product-item');
        allProductItems.forEach(item => {
          item.classList.remove('bundle-product-item--selected');
        });
        
        // Mark currently selected products in the grid
        this.state.selectedProducts.forEach((selection, selectionKey) => {
          const productId = selection.product.id;
          const groupId = selection.groupId;
          
          // Find the product item in the current group
          const productItem = this.element.querySelector(`[data-product-id="${productId}"][data-group-id="${groupId}"]`);
          
          if (productItem) {
            productItem.classList.add('bundle-product-item--selected');
          }
        });
      }
      
      
      handleGroupTabChange(event) {
        const radioInput = event.currentTarget;
        const groupId = radioInput.dataset.groupId;
        
        this.elements.groups.forEach(group => {
          const products = group.querySelectorAll('button.bundle-product-item');
          const images = group.querySelectorAll('img');
        });
        
        // Trigger smooth animated transition
        this.animateGroupTransition(groupId);
        
        // Clear product selection when changing groups for cleaner UX
        this.resetProductSelection();
        
        // Reinitialize carousels after group tab change
        this.reinitializeMobileCarousels();
        
        this.dispatchEvent('bundle:group_changed', {
          sectionId: this.sectionId,
          groupId: groupId
        });
      }
      
      animateGroupTransition(targetGroupId) {
        const currentGroup = this.element.querySelector('.bundle-builder__group:not(.bundle-builder__group--hidden)');
        // Fix: Select the actual group div, not the radio input
        const targetGroup = this.element.querySelector(`.bundle-builder__group[data-group-id="${targetGroupId}"]`);
        
        if (!targetGroup) {
          return;
        }
        
        
        // Step 1: Fade out current products if there's a current group
        if (currentGroup && currentGroup !== targetGroup) {
          const currentProducts = currentGroup.querySelectorAll('button.bundle-product-item');
          
          // Start fade out animation with will-change optimization
          currentProducts.forEach((item) => {
            item.classList.add('bundle-product-item--fade-out', 'bundle-product-item--animating');
            
            // Clean up will-change after fade-out completes
            setTimeout(() => {
              item.classList.remove('bundle-product-item--animating');
            }, this.ANIMATION_DELAYS.FADE_DURATION);
          });
          
          // Step 2: Switch groups after fade out animation
          setTimeout(() => {
            this.fadeInGroup(targetGroup, targetGroupId);
          }, this.ANIMATION_DELAYS.FADE_DURATION); // Wait for fade out animation
        } else {
          // No current group, just fade in new products
          this.fadeInGroup(targetGroup, targetGroupId);
        }
      }
      
      fadeInGroup(targetGroup, targetGroupId) {
        
        // Hide all groups
        this.elements.groups.forEach(group => {
          group.classList.add('bundle-builder__group--hidden');
        });
        
        // Show target group
        targetGroup.classList.remove('bundle-builder__group--hidden');
        
        // Wait a frame to ensure group is visible
        requestAnimationFrame(() => {
          const newProducts = targetGroup.querySelectorAll('button.bundle-product-item');
          
          // Apply staggered fade-in animation with dynamic delays
          newProducts.forEach((item, index) => {
            // Remove any existing classes and reset styles
            item.classList.remove('bundle-product-item--fade-out', 'bundle-product-item--fade-in', 'bundle-product-item--animate', 'bundle-product-item--animating');
            item.style.transitionDelay = '';
            
            // Set initial fade state and enable will-change
            item.classList.add('bundle-product-item--fade-in', 'bundle-product-item--animating');
            item.style.transitionDelay = `${index * this.ANIMATION_DELAYS.STAGGER_STEP}ms`;
          });
          
          // Step 2: Trigger staggered fade-in animation
          setTimeout(() => {
            newProducts.forEach((item) => {
              item.classList.add('bundle-product-item--animate');
            });
          }, 50); // Small delay to ensure initial state is applied
          
          
          // Clean up animation classes after completion with dynamic timing
          newProducts.forEach((item, index) => {
            setTimeout(() => {
              item.classList.remove('bundle-product-item--fade-in', 'bundle-product-item--animate', 'bundle-product-item--animating');
              item.style.transitionDelay = '';
            }, this.ANIMATION_DELAYS.FADE_DURATION + (index * this.ANIMATION_DELAYS.STAGGER_STEP));
          });
        });
      }
      
      handleSizeChange(event) {
        const newSize = event.target.value;
        
        this.state.currentSize = newSize;
        // Update the select item button state when size changes
        this.updateSelectItemButton();
        
        this.dispatchEvent('bundle:size_changed', {
          sectionId: this.sectionId,
          size: newSize,
          selectedCount: this.state.selectedProducts.size
        });
      }
      
      handleProductSelection(productItem) {
        const groupId = productItem.dataset.groupId;
        const productId = productItem.dataset.productId;
        const variantId = productItem.dataset.variantId;
        
        
        // Check if product is available in current size
        if (productItem.dataset.variantAvailable === 'false') {
          this.showError('This product is not available in the selected size.');
          return;
        }
        
        // Check if this product is already selected
        const currentSelection = this.state.selectedProducts.get(groupId);
        if (currentSelection && currentSelection.id === productId) {
          return;
        }
        
        // Clear previous selection in this group (visual only)
        this.clearGroupSelection(groupId);
        
        // Get product data from hidden form
        const form = productItem.querySelector('.bundle-product-item__form');
        const formData = new FormData(form);
        
        const productData = {
          id: productId,
          variantId: variantId,
          handle: formData.get('product_handle'),
          title: formData.get('product_title'),
          price: (() => {
            const priceValue = parseInt(formData.get('product_price'));
            return isNaN(priceValue) || priceValue < 0 ? 0 : priceValue;
          })(),
          compareAtPrice: (() => {
            const comparePriceValue = parseInt(formData.get('product_compare_at_price'));
            return isNaN(comparePriceValue) || comparePriceValue <= 0 ? null : comparePriceValue;
          })(),
          image: formData.get('product_image'),
          imageAlt: formData.get('product_image_alt'),
          size: this.state.currentSize,
          groupId: groupId
        };
        
        // Store selection (only one per group)
        this.state.selectedProducts.set(groupId, productData);
        
        // Update UI
        this.updateProductSelectionState(productItem, true);
        this.updateSelectionCard(groupId, productData);
        this.updateUI();
        
        // Show mobile summary if this is first selection
        if (this.state.selectedProducts.size === 1) {
          this.showMobileSummary();
        }
        
        this.dispatchEvent('bundle:product_selected', {
          sectionId: this.sectionId,
          groupId: groupId,
          productHandle: productData.handle,
          variantId: variantId,
          size: this.state.currentSize,
          selectedCount: this.state.selectedProducts.size
        });
      }
      
      handleLegacySelectionRemoval(selectionCard) {
        const groupId = selectionCard.dataset.groupId;
        
        // Remove from state
        this.state.selectedProducts.delete(groupId);
        
        // Update UI
        this.updateSelectionCard(groupId, null);
        this.updateProductSelectionState(null, false, groupId);
        this.updateUI();
        
        // Hide mobile summary if no selections
        if (this.state.selectedProducts.size === 0) {
          this.hideMobileSummary();
        }
        
        this.dispatchEvent('bundle:product_removed', {
          sectionId: this.sectionId,
          groupId: groupId,
          selectedCount: this.state.selectedProducts.size
        });
      }
      
      handleProductRemoval(groupId) {
        
        // Remove from selected products
        this.state.selectedProducts.delete(groupId);
        
        // Clear product selection UI if this was the currently selected product
        if (this.state.currentSelectedProduct) {
          const currentGroupId = this.getCurrentGroupId();
          if (currentGroupId === groupId) {
            this.resetProductSelection();
          }
        }
        
        // Update UI
        this.updateUI();
        
        // SCROLL TO TOP FOR BETTER UX
        this.scrollToTop();
        
        // Hide mobile summary if no selections
        if (this.state.selectedProducts.size === 0) {
          this.hideMobileSummary();
        }
        
        this.dispatchEvent('bundle:product_removed', {
          sectionId: this.sectionId,
          groupId: groupId,
          selectedCount: this.state.selectedProducts.size
        });
      }
      
      async handleAddToCart(event) {
        if (this.state.isLoading) return;
        
        
        // Validate required selections
        const missingRequired = this.validateRequiredSelections();
        if (missingRequired.length > 0) {
          this.showError(`Please select: ${missingRequired.join(', ')}`);
          return;
        }
        
        this.setLoading(true);
        
        try {
          const cartItems = this.buildCartItems();
          await this.addItemsToCart(cartItems);
          
          this.dispatchEvent('bundle:add_to_cart', {
            sectionId: this.sectionId,
            selectedCount: this.state.selectedProducts.size
          });
          
          // Trigger cart refresh and open side cart using theme's specific events
          document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
            bubbles: true
          }));
          
          // Use theme's cart notification system to open the drawer
          document.dispatchEvent(new CustomEvent('cart-notification:show', {
            bubbles: true,
            cancelable: true,
            detail: {
              status: 'success',
              openMiniCart: true,
              cart: null // Will be populated by theme
            }
          }));
          
          // Also try direct cart drawer opening
          const cartDrawer = document.getElementById('mini-cart');
          if (cartDrawer) {
            cartDrawer.open = true;
            cartDrawer.setAttribute('open', 'true');
          }
          
          // Fallback: click the cart trigger
          const cartTrigger = document.querySelector('[is="toggle-link"][aria-controls="mini-cart"], .header__icon-wrapper[aria-label*="cart"], [data-cart-trigger]');
          if (cartTrigger && cartTrigger.click) {
            setTimeout(() => cartTrigger.click(), this.ANIMATION_DELAYS.CART_OPEN_DELAY);
          }
          
        } catch (error) {
          this.showError('Failed to add items to cart. Please try again.');
        } finally {
          this.setLoading(false);
        }
      }
      
      
      updateProductVariantsForSize(newSize) {
        
        // Update all product items to show variants for the new size
        const productItems = this.getCachedProducts();
        
        productItems.forEach(item => {
          const productId = item.dataset.productId;
          const variants = this.getProductVariants(item);
          
          if (!variants || !Array.isArray(variants)) {
            return; // Skip this product
          }
          
          // Find variant that matches the new size
          let matchingVariant = null;
          let variantAvailable = false;
          
          // Search through variants for size match
          for (const variant of variants) {
            if (variant.options && variant.options.includes(newSize)) {
              matchingVariant = {
                id: variant.id,
                price: variant.price,
                compareAtPrice: variant.compare_at_price,
                available: variant.available,
                featuredMedia: variant.featured_media || ''
              };
              variantAvailable = variant.available;
              break;
            }
          }
          
          // Fallback to first variant if no size match
          if (!matchingVariant && variants.length > 0) {
            const firstVariant = variants[0];
            matchingVariant = {
              id: firstVariant.id,
              price: firstVariant.price,
              compareAtPrice: firstVariant.compare_at_price,
              available: firstVariant.available,
              featuredMedia: firstVariant.featured_media || ''
            };
            variantAvailable = false; // Mark as unavailable since it doesn't match size
          }
          
          // Update item state
          if (matchingVariant) {
            item.dataset.variantId = matchingVariant.id;
            item.dataset.variantAvailable = variantAvailable;
            
            // Update size status display
            const sizeStatus = item.querySelector('[data-size-status]');
            if (sizeStatus) {
              if (!variantAvailable) {
                sizeStatus.innerHTML = `<span class="text--small text--subdued">Not available in ${newSize}</span>`;
              } else {
                sizeStatus.innerHTML = '';
              }
            }
          }
          
          // Update visual state
          if (!variantAvailable) {
            item.classList.add('bundle-product-item--disabled');
          } else {
            item.classList.remove('bundle-product-item--disabled');
          }
          
          // Update selection state if this product is currently selected
          const groupId = item.dataset.groupId;
          if (this.state.selectedProducts.has(groupId)) {
            const selectedProduct = this.state.selectedProducts.get(groupId);
            if (selectedProduct.id === productId) {
              if (variantAvailable && matchingVariant) {
                // Update selected product data with new variant
                selectedProduct.variantId = matchingVariant.id;
                selectedProduct.price = matchingVariant.price;
                selectedProduct.compareAtPrice = matchingVariant.compareAtPrice;
                selectedProduct.size = newSize;
                if (matchingVariant.featuredMedia) {
                  selectedProduct.image = matchingVariant.featuredMedia;
                }
                this.state.selectedProducts.set(groupId, selectedProduct);
                
                // Update the selection card with new price
                this.updateSelectionCard(groupId, selectedProduct);
              } else {
                // Product no longer available in this size - show warning but keep selection
                this.showError(`The selected ${this.getGroupLabel(groupId)} is not available in size ${newSize}. Keeping previous selection.`);
              }
            }
          }
          
          // Update visual selection state
          this.updateProductSelectionState(item, this.state.selectedProducts.has(groupId) && this.state.selectedProducts.get(groupId).id === productId);
        });
      }
      
      clearGroupSelection(groupId) {
        // Remove visual selection from all products in this group
        const groupProducts = this.getCachedProducts(groupId);
        groupProducts.forEach(item => {
          item.classList.remove('bundle-product-item--selected');
        });
      }
      
      updateProductSelectionState(productItem, isSelected, groupId = null) {
        if (!productItem && groupId) {
          // Update all products in the group
          const groupProducts = this.getCachedProducts(groupId);
          groupProducts.forEach(item => {
            item.classList.remove('bundle-product-item--selected');
          });
          return;
        }
        
        if (!productItem) return;
        
        const itemGroupId = productItem.dataset.groupId;
        
        if (isSelected) {
          productItem.classList.add('bundle-product-item--selected');
        } else {
          productItem.classList.remove('bundle-product-item--selected');
        }
      }
      
      updateSelectionCard(groupId, productData) {
        const card = this.element.querySelector(`[data-group-id="${groupId}"]`);
        if (!card) return;
        
        if (productData) {
          card.classList.remove('bundle-builder__selection-card--empty');
          card.classList.add('bundle-builder__selection-card--selected');
          
          card.innerHTML = `
            <div class="bundle-builder__selection-content">
              <img src="${productData.image}" alt="${productData.imageAlt}" class="bundle-builder__selection-image" loading="lazy">
              <div class="bundle-builder__selection-info">
                <div class="bundle-builder__selection-title">${productData.title}</div>
                <div class="bundle-builder__selection-price">${this.formatMoney(productData.price)}</div>
              </div>
              <button type="button" class="bundle-builder__selection-remove" aria-label="${this.translations.accessibility?.removeSelection || 'Remove selection'}">
                <span class="visually-hidden">Remove</span>
                ×
              </button>
            </div>
          `;
        } else {
          card.classList.add('bundle-builder__selection-card--empty');
          card.classList.remove('bundle-builder__selection-card--selected');
          
          const groupLabel = card.dataset.groupLabel || (this.translations.labels?.product || 'Product');
          const isRequired = card.dataset.required === 'true';
          const placeholderText = isRequired 
            ? `Select ${groupLabel} (Required)`
            : `Select ${groupLabel} (Optional)`;
          
          card.innerHTML = `
            <div class="bundle-builder__selection-placeholder">
              <span class="bundle-builder__selection-label">${placeholderText}</span>
            </div>
          `;
        }
      }
      
      updateUI() {
        const previousCount = this.state.previousSelectedCount || 0;
        const currentCount = this.state.selectedProducts.size;
        
        this.updateCounts();
        this.updatePricing();
        this.updateSelectedProducts();
        this.updateSelectItemButton();
        this.updateAddToCartButtons();
        this.updateGroupTabStates();
        this.updateProductGridSelection();
        
        // Announce bundle state changes
        if (previousCount !== currentCount) {
          const isComplete = currentCount >= this.minimumProducts;
          this.announceBundleState(currentCount, this.maximumProducts, isComplete);
          this.state.previousSelectedCount = currentCount;
        }
      }
      
      updateCounts() {
        const selectedCount = this.state.selectedProducts.size;
        
        // Update bundle count in progress section (compact view)
        if (this.elements.bundleCount) {
          this.elements.bundleCount.textContent = selectedCount;
        }
        
        // Update detailed count display
        if (this.elements.countCurrent) {
          this.elements.countCurrent.textContent = selectedCount;
        }
        
        if (this.elements.mobileCount) {
          this.elements.mobileCount.textContent = `${selectedCount}/${this.getGroupCount()}`;
        }
      }
      
      updatePricing() {
        const totals = this.calculateTotals();
        const hasSelections = this.state.selectedProducts.size > 0;
        
        // Show/hide progress pricing section based on selections
        if (this.elements.progressPricing) {
          if (hasSelections) {
            this.elements.progressPricing.style.display = 'flex';
          } else {
            this.elements.progressPricing.style.display = 'none';
          }
        }
        
        // Update original total (compare-at price)
        if (this.elements.originalTotal) {
          if (hasSelections) {
            this.elements.originalTotal.textContent = this.formatMoney(totals.original);
            // Only show original total if there's a discount
            this.elements.originalTotal.style.display = totals.hasDiscount ? 'block' : 'none';
          } else {
            this.elements.originalTotal.style.display = 'none';
          }
        }
        
        // Show bundle total (final price) - update all instances
        if (this.elements.bundleTotal && this.elements.bundleTotal.length > 0) {
          this.elements.bundleTotal.forEach(element => {
            if (hasSelections) {
              element.textContent = this.formatMoney(totals.final);
              element.style.display = 'block';
            } else {
              element.style.display = 'none';
            }
          });
        }
        
        if (this.elements.finalTotal) {
          this.elements.finalTotal.textContent = this.formatMoney(totals.final);
        }
        
        if (this.elements.mobileTotal) {
          this.elements.mobileTotal.textContent = this.formatMoney(totals.final);
        }
        
        // Update discount messaging
        this.updateSavingsMessage(totals);
      }
      
      updateSelectedProducts() {
        if (!this.elements.selectedProducts) return;
        
        const hasSelections = this.state.selectedProducts.size > 0;
        
        if (hasSelections) {
          // Show the container and clear existing products
          this.elements.selectedProducts.classList.add('bundle-builder__selected-products--visible');
          this.elements.selectedProducts.innerHTML = '';
          
          // Add each selected product as a thumbnail
          this.state.selectedProducts.forEach((selection, selectionKey) => {
            const productThumbnail = document.createElement('div');
            productThumbnail.className = 'bundle-builder__product-thumbnail';
            
            // Get primary and secondary images
            const primaryImage = selection.product.featured_image || '';
            const secondaryImage = selection.product.secondary_image;
            
            // Format price for display (with sale price support)
            const currentPrice = selection.variant?.price ? selection.variant.price : selection.product.price_min;
            const comparePrice = selection.variant?.compare_at_price || selection.product.compare_at_price;
            
            let priceHtml = '';
            if (comparePrice && comparePrice > currentPrice) {
              priceHtml = `
                <span class="bundle-product-thumbnail__compare-price">${this.formatMoney(comparePrice)}</span>
                <span class="bundle-product-thumbnail__current-price">${this.formatMoney(currentPrice)}</span>
              `;
            } else {
              priceHtml = `<span class="bundle-product-thumbnail__current-price">${this.formatMoney(currentPrice)}</span>`;
            }
            
            productThumbnail.innerHTML = `
              <img 
                src="${primaryImage}" 
                alt="${selection.product.title} - ${selection.size}" 
                width="199" 
                height="249"
                class="bundle-product-thumbnail__image--primary"
                title="${selection.product.title} - ${selection.size}">
              ${secondaryImage && secondaryImage !== primaryImage ? `
                <img 
                  src="${secondaryImage}" 
                  alt="${selection.product.title} - ${selection.size}" 
                  width="199" 
                  height="249"
                  class="bundle-product-thumbnail__image--secondary"
                  title="${selection.product.title} - ${selection.size}">
              ` : ''}
              <div class="bundle-product-thumbnail__overlay">
                <div class="bundle-product-thumbnail__title">${selection.product.title}</div>
                <div class="bundle-product-thumbnail__size">Size: ${selection.size}</div>
                <div class="bundle-product-thumbnail__price">${priceHtml}</div>
              </div>
              <button 
                type="button" 
                class="bundle-builder__product-remove" 
                data-selection-key="${selectionKey}"
                aria-label="Remove ${selection.product.title} from bundle"
                title="${this.translations.accessibility?.removeFromBundle || 'Remove from bundle'}">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="17" viewBox="0 0 16 17" fill="none" style="transform: rotate(45deg);">
                  <path d="M8 3.91602V13.2494" stroke="currentColor" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M3.33008 8.58008H12.6634" stroke="currentColor" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </button>
            `;
            this.elements.selectedProducts.appendChild(productThumbnail);
          });
        } else {
          // Hide the entire container when no products are selected
          this.elements.selectedProducts.classList.remove('bundle-builder__selected-products--visible');
        }
      }
      
      updateSelectItemButton() {
        if (!this.elements.selectItemBtn) return;
        
        const currentProduct = this.state.currentSelectedProduct;
        const selectedSize = this.getSelectedSizeValue();
        const selectedCount = this.state.selectedProducts.size;
        
        // Check if product has size options available
        const hasSizeOptions = this.elements.productSizes && 
          this.elements.productSizes.innerHTML !== '<p class="text--small text--subdued">No size options available</p>' &&
          this.elements.productSizes.querySelector('input[type="radio"]');
        
        // Determine if we have what we need to proceed
        const hasProductAndRequiredSelection = currentProduct && (hasSizeOptions ? selectedSize : true);
        
        // Check if bundle is at maximum capacity
        if (selectedCount >= this.maximumProducts) {
          this.elements.selectItemBtn.disabled = true;
          this.elements.selectItemBtn.textContent = this.translations.buttons?.bundleFull || 'Bundle Full';
          
          // Hide size section when bundle is full
          if (this.elements.productSizes) {
            this.elements.productSizes.style.display = 'none';
          }
          return;
        }
        
        // Show size section when bundle is not full
        if (this.elements.productSizes) {
          this.elements.productSizes.style.display = 'block';
        }
        
        // Check if the selected size is available using Liquid-rendered data
        let isAvailable = false;
        if (hasProductAndRequiredSelection) {
          if (hasSizeOptions) {
            // Get the checked size input and read its availability from Liquid data
            const checkedSizeInput = this.elements.productSizes?.querySelector('input[type="radio"]:checked');
            if (checkedSizeInput) {
              isAvailable = checkedSizeInput.dataset.variantAvailable === 'true';
            }
          } else {
            // Products without size options are considered available by default
            isAvailable = true;
          }
        }
        
        // Update button state - use theme's standard button--secondary styling like PDP
        
        if (hasProductAndRequiredSelection && isAvailable) {
          // Product and (size if required) selected, and available
          this.elements.selectItemBtn.disabled = false;
          this.elements.selectItemBtn.textContent = this.translations.buttons?.addToBundle || 'Add to bundle';
        } else if (hasProductAndRequiredSelection && !isAvailable) {
          // Product and (size if required) selected, but not available - matches PDP behavior
          this.elements.selectItemBtn.disabled = true;
          this.elements.selectItemBtn.textContent = this.translations.buttons?.outOfStock || 'Out of stock';
        } else if (currentProduct && hasSizeOptions && !selectedSize) {
          // Product selected but no size (only show this message if size is required)
          this.elements.selectItemBtn.disabled = true;
          this.elements.selectItemBtn.textContent = this.translations.buttons?.selectSize || 'Select a size';
        } else {
          // No product selected
          this.elements.selectItemBtn.disabled = true;
          this.elements.selectItemBtn.textContent = this.translations.buttons?.selectProduct || 'Select a product';
        }
      }
      
      updateAddToCartButtons() {
        const canAddToCart = this.canAddToCart();
        
        if (this.elements.addToCartBtn) {
          this.elements.addToCartBtn.disabled = !canAddToCart;
        }
        
        if (this.elements.mobileAddToCartBtn) {
          this.elements.mobileAddToCartBtn.disabled = !canAddToCart;
        }
      }
      
      handleProductRemoval(selectionKey) {
        
        // Remove from selected products using the unique key
        this.state.selectedProducts.delete(selectionKey);
        
        // Announce removal to screen readers
        const currentCount = this.state.selectedProducts.size;
        const totalCount = this.maximumProducts;
        this.announceProductRemoved(currentCount, totalCount);
        
        // Update UI
        this.updateUI();
        
        // SCROLL TO TOP FOR BETTER UX
        this.scrollToTop();
        
        // Hide mobile summary if no selections
        if (this.state.selectedProducts.size === 0) {
          this.hideMobileSummary();
        }
        
        // Dispatch removal event
        this.dispatchEvent('bundle:product_removed', {
          sectionId: this.sectionId,
          selectionKey: selectionKey,
          selectedCount: this.state.selectedProducts.size
        });
      }
      
      handleProductClick(productItem) {
        
        // Clear focus state from all products
        this.element.querySelectorAll('.bundle-product-item--focused').forEach(item => {
          item.classList.remove('bundle-product-item--focused');
        });
        
        // Add focus state to clicked product
        productItem.classList.add('bundle-product-item--focused');
        
        // Hide all variant pickers first
        this.element.querySelectorAll('[data-product-variant-picker]').forEach(picker => {
          picker.style.display = 'none';
        });
        
        // Load variant picker for this product
        const productData = this.getProductDataFromElement(productItem);
        
        if (productData) {
          // Store current selected product
          this.state.currentSelectedProduct = productData;
          
          // Announce product selection to screen readers
          this.announceProductSelection(productData.title);
          
          // Load and render the variant picker
          this.loadProductVariantPicker(productData);
          
          // Show the product selection area
          if (this.elements.productSelection) {
            this.elements.productSelection.style.display = 'block';
          }
          
          // Update button state after product selection
          this.updateSelectItemButton();
        }
      }
      
      getProductDataFromElement(productItem) {
        // Extract product data from data attributes (efficient JSON approach)
        const productData = {
          id: productItem.dataset.productId,
          handle: productItem.dataset.productHandle,
          title: productItem.dataset.productTitle,
          price_min: (() => {
            const priceValue = parseInt(productItem.dataset.productPrice);
            if (isNaN(priceValue) || priceValue < 0) {
              console.error('Invalid price data for product:', productItem.dataset.productId, productItem.dataset.productTitle);
              return 0; // Fallback to 0 but log error for debugging
            }
            return priceValue;
          })(),
          featured_image: productItem.dataset.productImage,
          options: this.getProductOptions(productItem),
          variants: this.getProductVariants(productItem)
        };
        
        // Extract secondary image from the HTML structure
        const secondaryImageElement = productItem.querySelector('.bundle-product-item__image--secondary');
        if (secondaryImageElement && secondaryImageElement.src) {
          productData.secondary_image = secondaryImageElement.src;
        } else {
          productData.secondary_image = null; // No secondary image available
        }
        
        return productData;
      }
      
      getProductOptions(productElement) {
        if (!productElement || !productElement.dataset) {
          return [];
        }
        
        const optionsData = productElement.dataset.productOptions;
        if (!optionsData) {
          return [];
        }
        
        try {
          const parsed = JSON.parse(optionsData);
          if (!Array.isArray(parsed)) {
            return [];
          }
          return parsed;
        } catch (e) {
          return [];
        }
      }
      
      getProductVariants(productElement) {
        if (!productElement || !productElement.dataset) {
          return [];
        }
        
        const variantData = productElement.dataset.variantData;
        if (!variantData) {
          return [];
        }
        
        // Check if variant data is empty or just whitespace
        if (variantData.trim() === '' || variantData.trim() === '[]') {
          return [];
        }
        
        try {
          const parsed = JSON.parse(variantData);
          if (!Array.isArray(parsed)) {
            return [];
          }
          return parsed;
        } catch (e) {
          return [];
        }
      }
      
      extractSizeValues(variants, optionIndex) {
        if (!variants || !Array.isArray(variants)) {
          return [];
        }
        
        const sizeValues = new Set();
        
        variants.forEach((variant) => {
          if (variant.options && variant.options[optionIndex]) {
            const sizeValue = variant.options[optionIndex];
            sizeValues.add(sizeValue);
          }
        });
        
        return Array.from(sizeValues);
      }
      
      findVariantBySize(product, size) {
        if (!product.variants || !size) return null;
        
        return product.variants.find(variant => {
          return variant.options && variant.options.includes(size);
        });
      }
      
      getSelectedSizeValue() {
        // Simple method to get the selected size value from radio inputs
        const checkedInput = this.elements.productSizes?.querySelector('input[type="radio"]:checked');
        return checkedInput ? checkedInput.value : null;
      }
      
      updateSavingsMessage(totals) {
        if (!this.elements.savingsMessage || this.elements.savingsMessage.length === 0) return;
        
        // Check if discount messaging is enabled
        if (!this.discountSettings.showMessaging) {
          this.elements.savingsMessage.forEach(element => {
            element.style.display = 'none';
          });
          return;
        }
        
        const selectedCount = this.state.selectedProducts.size;
        
        if (selectedCount >= this.minimumProducts) {
          // Customer qualifies for discount
          this.elements.savingsMessage.forEach(element => {
            element.textContent = this.discountSettings.qualifiedMessage;
            element.style.display = 'block';
          });
        } else if (selectedCount > 0) {
          // Show progress toward discount
          const remaining = this.minimumProducts - selectedCount;
          const message = this.discountSettings.progressMessage.replace('{count}', remaining);
          this.elements.savingsMessage.forEach(element => {
            element.textContent = message;
            element.style.display = 'block';
          });
        } else {
          // No products selected
          this.elements.savingsMessage.forEach(element => {
            element.style.display = 'none';
          });
        }
      }
      
      calculateTotals() {
        const selectedProducts = Array.from(this.state.selectedProducts.values());
        
        // Calculate totals considering both individual product compare-at prices and bundle discount
        let originalTotal = 0; // Sum of individual compare-at prices or regular prices
        let subtotal = 0; // Sum of regular prices (for bundle discount calculation)
        
        selectedProducts.forEach(product => {
          // For original total: use compare-at price if available, otherwise use current price
          // This shows what the bundle would cost at full individual prices
          const originalPrice = product.compareAtPrice || product.price;
          originalTotal += originalPrice;
          
          // For subtotal: always use the current selling price (for bundle discount calculation)
          subtotal += product.price;
        });
        
        // Check if bundle discount conditions are met
        const selectedCount = selectedProducts.length;
        const qualifiesForDiscount = selectedCount >= this.minimumProducts;
        
        let finalTotal = originalTotal;
        let hasDiscount = false;
        
        if (qualifiesForDiscount && this.discountSettings.showMessaging) {
          // Apply percentage discount to the subtotal (regular prices)
          const discountAmount = subtotal * (this.discountSettings.percentage / 100);
          finalTotal = subtotal - discountAmount;
          hasDiscount = true;
        }
        
        return {
          original: originalTotal,
          subtotal: subtotal,
          final: finalTotal,
          hasDiscount: hasDiscount,
          qualifiesForDiscount: qualifiesForDiscount,
          selectedCount: selectedCount
        };
      }
      
      
      getGroupCount() {
        return this.elements.groups.length;
      }
      
      getGroupLabel(groupId) {
        const group = this.element.querySelector(`[data-group-id="${groupId}"]`);
        return group ? group.dataset.groupLabel || 'Product' : 'Product';
      }
      
      validateRequiredSelections() {
        // For flexible bundles, we don't enforce group-based requirements
        // Just return empty array since validation is handled by minimum/maximum product counts
        return [];
      }
      
      canAddToCart() {
        const selectedCount = this.state.selectedProducts.size;
        const hasRequiredSelections = this.validateRequiredSelections().length === 0;
        
        // Check if we have any products selected
        if (selectedCount === 0) return false;
        
        // Check if we exceed maximum
        if (selectedCount > this.maximumProducts) return false;
        
        // If partial bundles are allowed, only need 1+ products and required selections
        if (this.allowPartialBundles) {
          return hasRequiredSelections;
        }
        
        // Otherwise, need minimum products and required selections
        return selectedCount >= this.minimumProducts && hasRequiredSelections;
      }
      
      buildCartItems() {
        const items = [];
        
        this.state.selectedProducts.forEach(selection => {
          
          items.push({
            id: selection.variant.id,
            quantity: 1,
            properties: {
              [this.bundleTagKey]: this.bundleTagValue  // Apply bundle tag key and value to all products added in the bundle to match SupaEasy 'Cart attribute key' and 'Cart attribute values'
            }
          });
        });
        
        return items;
      }
      
      async addItemsToCart(items) {
        // Pre-validate inventory for each item
        await this.validateInventory(items);
        
        // Add timeout handling for cart operations
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
        
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({ items }),
          signal: controller.signal
        });
        
        clearTimeout(timeoutId);
        
        if (!response.ok) {
          const error = await response.json();
          
          // Handle specific error types
          if (error.message && error.message.includes('not available')) {
            throw new Error('One or more products are no longer available in the selected size.');
          } else if (error.message && error.message.includes('inventory')) {
            throw new Error('Insufficient inventory for one or more products.');
          } else if (error.message && error.message.includes('variant')) {
            throw new Error('Invalid product variant selected.');
          } else {
            throw new Error(error.message || (this.translations.messages?.failedAddToCart || 'Failed to add items to cart'));
          }
        }
        
        return response.json();
      }
      
      async validateInventory(items) {
        // Check each variant's current availability
        for (const item of items) {
          try {
            // Add timeout for product validation
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout
            
            const response = await fetch(`/products/${item.id}.js`, {
              signal: controller.signal
            });
            
            clearTimeout(timeoutId);
            if (response.ok) {
              const variant = await response.json();
              if (!variant.available) {
                throw new Error(`Selected variant is no longer available.`);
              }
            }
          } catch (error) {
            // Continue with cart add - let Shopify handle the validation
          }
        }
      }
      
      setLoading(loading) {
        this.state.isLoading = loading;
        
        if (loading) {
          this.element.classList.add('bundle-builder--loading');
          this.announceLoadingState('Adding items to cart, please wait...');
        } else {
          this.element.classList.remove('bundle-builder--loading');
          this.announceLoadingState('Items added successfully');
        }
      }
      
      showError(message) {
        
        if (this.elements.errorMessage) {
          this.elements.errorMessage.textContent = message;
          this.elements.errorMessage.style.display = 'block';
          
          // Make error focusable and move focus to it
          this.elements.errorMessage.setAttribute('tabindex', '-1');
          this.elements.errorMessage.focus();
          
          // Auto-hide after 5 seconds
          setTimeout(() => {
            this.elements.errorMessage.style.display = 'none';
            this.elements.errorMessage.removeAttribute('tabindex');
          }, 5000);
        }
      }
      
      showMobileSummary() {
        if (this.elements.mobileSummary) {
          this.elements.mobileSummary.style.display = 'block';
        }
      }
      
      hideMobileSummary() {
        if (this.elements.mobileSummary) {
          this.elements.mobileSummary.style.display = 'none';
        }
      }
      
      formatMoney(cents) {
        // Handle invalid values
        if (!cents || isNaN(cents) || cents === null || cents === undefined) {
          return '£0.00';
        }
        
        // Ensure it's a number
        const numericCents = parseInt(cents) || 0;
        
        // Use theme's money formatting if available
        if (window.Shopify && window.Shopify.formatMoney) {
          return window.Shopify.formatMoney(numericCents);
        }
        
        // Fallback formatting - use shop's currency
        const currency = window.BundleBuilderConfig?.shopCurrency || 'GBP';
        const locale = currency === 'GBP' ? 'en-GB' : 'en-US';
        
        return new Intl.NumberFormat(locale, {
          style: 'currency',
          currency: currency
        }).format(numericCents / 100);
      }
      
      // Removed old async handleProductClick method - using simplified version below
      
      handleSelectItem(event) {
        event.preventDefault();
        
        // Get current selected product and size
        const selectedSize = this.getSelectedSizeValue();
        const currentProduct = this.state.currentSelectedProduct;
        
        if (!currentProduct) {
          this.showError(this.translations.messages?.selectProductFirst || 'Please select a product first');
          return;
        }
        
        // Check if product has size options available
        const hasSizeOptions = this.elements.productSizes && 
          this.elements.productSizes.innerHTML !== '<p class="text--small text--subdued">No size options available</p>' &&
          this.elements.productSizes.querySelector('input[type="radio"]');
        
        // Only require size selection if the product has size options
        if (hasSizeOptions && !selectedSize) {
          this.showError(this.translations.messages?.selectSize || 'Please select a size');
          return;
        }
        
        // Use 'default' as size for products without size options
        const sizeToUse = selectedSize || 'default';
        
        // Add to bundle
        this.addProductToBundle(currentProduct, sizeToUse);
        
        // Reset selection UI
        this.resetProductSelection();
      }
      
      getCurrentSelectedSize() {
        const sizeInputs = this.elements.productSizes?.querySelectorAll('input[type="radio"]:checked');
        return sizeInputs?.[0]?.value || null;
      }
      
      getCurrentSelectedProduct() {
        // This should be stored when a product is clicked
        return this.state.currentSelectedProduct || null;
      }
      
      addProductToBundle(product, size) {
        // Check if we've reached the maximum products limit
        if (this.state.selectedProducts.size >= this.maximumProducts) {
          this.showError(`Maximum ${this.maximumProducts} products allowed in bundle`);
          return;
        }
        
        // Check if current group already has a selection
        const currentGroupId = this.getCurrentGroupId();
        const existingSelection = Array.from(this.state.selectedProducts.entries())
          .find(([key, selection]) => selection.groupId === currentGroupId);
        
        // Track if we're switching products (for announcement)
        const isSwitching = !!existingSelection;
        
        // If group already has a selection, remove it first (product switching)
        if (existingSelection) {
          const [existingKey] = existingSelection;
          this.state.selectedProducts.delete(existingKey);
        }
        
        // Check if product has size options available
        const hasSizeOptions = this.elements.productSizes && 
          this.elements.productSizes.innerHTML !== '<p class="text--small text--subdued">No size options available</p>' &&
          this.elements.productSizes.querySelector('input[type="radio"]');
        
        let selectedInput = null;
        if (hasSizeOptions) {
          // Get variant data from the selected size input (Liquid-rendered data)
          selectedInput = this.elements.productSizes?.querySelector('input[type="radio"]:checked');
          if (!selectedInput) {
            this.showError('Please select a size');
            return;
          }
        }
        
        // Get variant information - different logic for products with/without sizes
        let variantId = null;
        let isAvailable = true;
        
        if (hasSizeOptions && selectedInput) {
          variantId = selectedInput.dataset.variantId;
          isAvailable = selectedInput.dataset.variantAvailable === 'true';
          
          if (!variantId || !isAvailable) {
            this.showError(this.translations.messages?.sizeNotAvailable || 'Selected size is not available');
            return;
          }
        } else {
          // For products without sizes, find the first available variant
          const currentProduct = this.state.currentSelectedProduct;
          if (currentProduct && currentProduct.variants && currentProduct.variants.length > 0) {
            const firstVariant = currentProduct.variants[0];
            variantId = firstVariant.id;
            isAvailable = firstVariant.available;
          }
        }
        
        // Get variant price data from the currently selected product
        const currentProduct = this.state.currentSelectedProduct;
        if (!currentProduct) {
          this.showError(this.translations.messages?.noProductSelected || 'No product selected');
          return;
        }
        
        const variants = currentProduct.variants || [];
        
        if (!variants || !Array.isArray(variants)) {
          this.showError(this.translations.messages?.invalidVariantData || 'Product variant data is invalid');
          return;
        }
        
        const selectedVariant = variants.find(v => v.id == variantId);
        
        let variantPrice = product.price_min;
        let variantCompareAtPrice = null;
        
        if (selectedVariant) {
          variantPrice = selectedVariant.price;
          variantCompareAtPrice = selectedVariant.compare_at_price;
        }
        
        // Create variant object from Liquid data
        const variant = {
          id: parseInt(variantId),
          available: isAvailable,
          price: variantPrice,
          compare_at_price: variantCompareAtPrice
        };
        
        // Create unique product selection key (product ID + variant ID for uniqueness)
        const selectionKey = `${product.id}_${variant.id}`;
        
        // Create product selection
        const selection = {
          selectionKey: selectionKey,
          groupId: this.getCurrentGroupId(),
          product: product,
          variant: variant,
          size: size,
          price: variant.price
        };
        
        
        // Store selection with unique key
        this.state.selectedProducts.set(selectionKey, selection);
        
        // Mark the selected product in the grid with visual feedback
        this.updateProductGridSelection();
        
        // Announce successful addition/switching to screen readers
        const currentCount = this.state.selectedProducts.size;
        const totalCount = this.maximumProducts;
        if (isSwitching) {
          this.announceProductSwitched(product.title, size, currentCount, totalCount);
        } else {
          this.announceProductAdded(product.title, size, currentCount, totalCount);
        }
        
        // Update UI
        this.updateUI();
        
        // AUTO-ADVANCE TO NEXT EMPTY GROUP TAB
        this.advanceToNextEmptyGroup();
        
        // SCROLL TO TOP FOR BETTER UX
        this.scrollToTop();
      }
      
      resetProductSelection() {
        // Hide the product selection area
        if (this.elements.productSelection) {
          this.elements.productSelection.style.display = 'none';
        }
        
        // Clear size selection
        if (this.elements.productSizes) {
          this.elements.productSizes.innerHTML = `
            <div class="bundle-builder__size-placeholder">
              <span class="bundle-builder__size-label">${this.translations.messages?.selectProduct || 'Select a product to choose size'}</span>
            </div>
          `;
        }
        
        // Clear current product state
        this.state.currentSelectedProduct = null;
      }
      
      getCurrentGroupId() {
        const checkedTab = this.element.querySelector('input[name^="bundle-group"]:checked');
        return checkedTab?.dataset.groupId || null;
      }
      
      findVariantBySize(product, size) {
        // Find variant that matches the selected size
        // In Shopify product JSON, variants have options array with values, not objects
        return product.variants?.find(variant => {
          if (!variant.options || !product.options) return false;
          
          // Find the size option index
          const sizeOptionIndex = product.options.findIndex(option => 
            option.name?.toLowerCase() === 'size' || 
            option.name?.toLowerCase() === 'taille' || 
            option.name?.toLowerCase() === 'talla'
          );
          
          if (sizeOptionIndex === -1) return false;
          
          // Check if the variant's option at this index matches the size
          return variant.options[sizeOptionIndex] === size;
        });
      }
      
      findVariantByOptions(productData, optionValues) {
        // Find variant that matches the given option values
        return productData.variants?.find(variant => {
          // Check if all option values match
          return Object.entries(optionValues).every(([optionName, optionValue]) => {
            // Find the option position for this option name
            const optionIndex = productData.options.findIndex(opt => opt.name === optionName);
            if (optionIndex === -1) return false;
            
            // Check if the variant's option at this position matches the value
            return variant.options && variant.options[optionIndex] === optionValue;
          });
        });
      }
      
      showProductSelectionLoading() {
        if (!this.elements.productSizes) return;
        
        this.elements.productSizes.innerHTML = `
          <div class="bundle-product-selection-loading">
            <div class="spinner">
              <svg focusable="false" width="24" height="24" class="icon icon--spinner" viewBox="25 25 50 50">
                <circle cx="50" cy="50" r="20" fill="none" stroke="currentColor" stroke-width="5"></circle>
              </svg>
            </div>
            <p>Loading size options...</p>
          </div>
        `;
        this.elements.productSizes.style.display = 'block';
      }
      
      showProductSelectionError(message) {
        if (!this.elements.productSizes) return;
        
        this.elements.productSizes.innerHTML = `
          <div class="bundle-product-selection-error">
            <p class="text--small text--error">${message}</p>
            <button type="button" class="button button--small" onclick="this.parentElement.parentElement.style.display='none'">Close</button>
          </div>
        `;
        this.elements.productSizes.style.display = 'block';
      }
      
      loadProductVariantPicker(productData) {
        try {
          
          // Use existing product data instead of fetching from Shopify
          
          // Render the variant picker with existing data
          this.renderVariantPicker(productData);
          
        } catch (error) {
          this.showProductSelectionError(error.message);
        }
      }
      
      renderVariantPicker(product) {
        
        if (!this.elements.productSizes) {
          return;
        }
        
        // Check if product has variants and options
        if (!product.variants || !Array.isArray(product.variants) || product.variants.length === 0) {
          this.elements.productSizes.innerHTML = '<p class="text--small text--subdued">No size options available</p>';
          return;
        }
        
        if (!product.options || !Array.isArray(product.options)) {
          this.elements.productSizes.innerHTML = '<p class="text--small text--subdued">No size options available</p>';
          return;
        }
        
        
        // Find the size option with flexible detection
        let sizeOption = null;
        let sizeOptionIndex = -1;
        
        // Handle different option formats
        if (Array.isArray(product.options)) {
          // Check if options is array of strings (Shopify product.options format)
          if (typeof product.options[0] === 'string') {
            
            // Find size option by name
            for (let i = 0; i < product.options.length; i++) {
              const optionName = product.options[i].toLowerCase();
              if (['size', 'taille', 'talla', 'dimensione', 'grosse', 'koko', 'サイズ', '尺寸', 'размер', 'размер', '尺码'].includes(optionName)) {
                sizeOptionIndex = i;
                sizeOption = {
                  name: product.options[i],
                  position: i + 1,
                  values: this.extractSizeValues(product.variants, i)
                };
                break;
              }
            }
          } else {
            // Handle object format (if it exists)
            for (const option of product.options) {
              if (!option) continue;
              
              // Check if option has name property
              if (option.name && typeof option.name === 'string') {
                const name = option.name.toLowerCase();
                if (['size', 'taille', 'talla', 'dimensione', 'grosse', 'koko', 'サイズ', '尺寸', 'размер', 'размер', '尺码'].includes(name)) {
                  sizeOption = option;
                  break;
                }
              }
              
              // Check if option has position property (Shopify structure)
              if (option.position && option.values && Array.isArray(option.values)) {
                // This might be a size option if it has common size values
                const values = option.values.map(v => v.toLowerCase());
                const commonSizes = ['xs', 's', 'm', 'l', 'xl', 'xxl', '2xl', '3xl', '4xl', '5xl', 'small', 'medium', 'large'];
                if (values.some(v => commonSizes.includes(v))) {
                  sizeOption = option;
                  break;
                }
              }
            }
          }
        }
        
        if (!sizeOption) {
          this.elements.productSizes.innerHTML = '<p class="text--small text--subdued">No size options available</p>';
          return;
        }
        
        // Generate size options HTML
        let sizeOptionsHtml = '';
        
        // Get size values from the option
        const sizeValues = sizeOption.values || [];
        
        sizeValues.forEach((sizeValue, index) => {
          // Find variant for this size using the correct option index
          let variant = null;
          
          // Use the option index we found earlier
          if (sizeOptionIndex >= 0) {
            variant = product.variants.find(v => v.options && v.options[sizeOptionIndex] === sizeValue);
          }
          
          // Fallback: search by size value in options array
          if (!variant) {
            variant = product.variants.find(v => v.options && v.options.includes(sizeValue));
          }
          
          const isAvailable = variant ? variant.available : false;
          
          // Don't auto-select any size - let user choose for clearer UX
          
          const disabledAttr = !isAvailable ? 'aria-disabled="true"' : '';
          const ariaLabel = !isAvailable ? `aria-label="Size ${sizeValue} (Out of stock)"` : `aria-label="Size ${sizeValue}"`;
          
          sizeOptionsHtml += `
            <div class="block-swatch${!isAvailable ? ' is-disabled' : ''}">
              <input 
                class="block-swatch__radio visually-hidden" 
                type="radio" 
                name="bundle-size-${product.id}" 
                id="size-${product.id}-${index}" 
                value="${sizeValue}" 
                data-variant-id="${variant ? variant.id : ''}"
                data-variant-available="${isAvailable}"
                ${disabledAttr}
                ${ariaLabel}>
              <label class="block-swatch__item bold" for="size-${product.id}-${index}">${sizeValue}</label>
            </div>
          `;
        });
        
        // Generate complete variant picker HTML
        const optionName = sizeOption.name || (this.translations.labels?.size || 'Size');
        
        const variantPickerHtml = `
          <div class="bundle-product-option" data-option-name="${optionName}" role="group" aria-labelledby="size-label-${product.id}">
            <div class="bundle-product-option__info">
              <a href="/products/${product.handle}" class="bundle-product-option__product-link link link--accented">${product.title}</a>
              <span id="size-label-${product.id}" class="bundle-product-option__name">${optionName}</span>
              <span id="size-value-${product.id}" class="bundle-product-option__value bold" aria-live="polite"></span>
            </div>
            <fieldset class="block-swatch-list" aria-labelledby="size-label-${product.id}">
              <legend class="visually-hidden">${this.translations.accessibility?.selectSizeLegend || 'Select'} ${optionName.toLowerCase()} for ${product.title}</legend>
              ${sizeOptionsHtml}
            </fieldset>
          </div>
        `;
        
        // Insert the HTML
        this.elements.productSizes.innerHTML = variantPickerHtml;
        this.elements.productSizes.style.display = 'block';
        
        // Set up event listeners
        this.setupSizeOptionListeners(this.elements.productSizes, product);
        
        // Update button text to show "Select a size" since no size is auto-selected
        this.updateSelectItemButton();
        
      }
      
      setupSizeOptionListeners(sizeSection, productData) {
        const sizeInputs = sizeSection.querySelectorAll('input[type="radio"]');
        
        sizeInputs.forEach(input => {
          input.addEventListener('change', (e) => {
            
            // Update the size value display
            const valueDisplayId = `size-value-${productData.id}`;
            const valueDisplay = document.getElementById(valueDisplayId);
            if (valueDisplay) {
              valueDisplay.textContent = e.target.value;
            }
            
            // Update the select item button state when size changes
            this.updateSelectItemButton();
            
            // Focus the "Add to bundle" button when size is selected
            this.moveFocusToElement(this.elements.selectItemBtn);
          });
        });
        
        // Set initial value for the checked input
        const checkedInput = sizeSection.querySelector('input[type="radio"]:checked');
        if (checkedInput) {
          const bindValueId = checkedInput.dataset.bindValue;
          const valueDisplay = document.getElementById(bindValueId);
          if (valueDisplay) {
            valueDisplay.textContent = checkedInput.value;
          }
        }
      }
      
      dispatchEvent(eventName, detail) {
        const event = new CustomEvent(eventName, {
          detail: { ...detail, sectionId: this.sectionId },
          bubbles: true
        });
        
        this.element.dispatchEvent(event);
        
        // Also dispatch on document for global listeners
        document.dispatchEvent(event);
      }
      
      // Accessibility announcement methods
      announceToScreenReader(message, priority = 'polite') {
        const announcement = priority === 'assertive' 
          ? this.elements.loadingAnnouncement 
          : this.elements.selectionAnnouncement;
        
        if (announcement) {
          announcement.textContent = message;
          // Clear after a brief moment to allow re-announcement of same message
          setTimeout(() => {
            announcement.textContent = '';
          }, 1000);
        }
      }
      
      announceProductSelection(productTitle) {
        this.announceToScreenReader(`${productTitle} selected. Choose a size to add to bundle.`);
      }
      
      announceProductAdded(productTitle, size, currentCount, totalCount) {
        this.announceToScreenReader(`${productTitle} in size ${size} added. ${currentCount} of ${totalCount} products selected.`);
      }
      
      announceProductSwitched(productTitle, size, currentCount, totalCount) {
        this.announceToScreenReader(`${productTitle} in size ${size} switched. ${currentCount} of ${totalCount} products selected.`);
      }
      
      announceProductRemoved(currentCount, totalCount) {
        this.announceToScreenReader(`Product removed. ${currentCount} of ${totalCount} products selected.`);
      }
      
      announceLoadingState(message) {
        this.announceToScreenReader(message, 'assertive');
      }
      
      announceBundleState(currentCount, totalCount, isComplete = false) {
        let message = `Bundle: ${currentCount} of ${totalCount} products selected.`;
        if (isComplete) {
          message += ' Bundle complete, ready to add to cart.';
        } else {
          const remaining = totalCount - currentCount;
          message += ` Add ${remaining} more to complete bundle.`;
        }
        this.announceToScreenReader(message);
      }
      
      // Enhanced focus management
      moveFocusToElement(element, delay = 100) {
        if (!element) return;
        
        setTimeout(() => {
          element.focus();
        }, delay);
      }
      
      scrollToTop() {
        const headerHeight = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 0;
        const elementTop = this.element.getBoundingClientRect().top + window.pageYOffset;
        const scrollToPosition = elementTop - headerHeight - 20; // 20px breathing room
        
        window.scrollTo({
          top: scrollToPosition,
          behavior: 'smooth'
        });
      }
      
      moveFocusToProductGrid() {
        const firstVisibleProduct = this.element.querySelector('.bundle-product-item:not(.bundle-product-item--hidden):not(.bundle-product-item--hidden-gender)');
        if (firstVisibleProduct) {
          this.moveFocusToElement(firstVisibleProduct);
        }
      }
      
      moveFocusToAddToCart() {
        const addToCartBtn = this.elements.addToCartBtn || this.elements.mobileAddToCartBtn;
        if (addToCartBtn && !addToCartBtn.disabled) {
          this.moveFocusToElement(addToCartBtn);
        }
      }
      
      // ==========================================================================
      // CAROUSEL FUNCTIONALITY (Mobile Only)
      // ==========================================================================
      
      initCarousel() {
        // Only initialize if we're on mobile and in carousel mode
        if (window.innerWidth > 999) return;
        
        const groups = this.element.querySelectorAll('.bundle-builder__group');
        groups.forEach(group => {
          const productsContainer = group.querySelector('.bundle-builder__products-inner');
          const navigation = group.querySelector('.bundle-builder__carousel-nav');
          
          if (!productsContainer || !navigation) return;
          
          // Get visible products (not hidden by gender filter or pagination)
          const visibleProducts = Array.from(productsContainer.querySelectorAll('.bundle-product-item')).filter(item => {
            return !item.classList.contains('bundle-product-item--hidden-gender') && !item.classList.contains('bundle-product-item--hidden');
          });
          
          // Skip if ≤1 product (no navigation needed)
          if (visibleProducts.length <= 1) {
            navigation.style.display = 'none';
            return;
          }
          
          // Show navigation and generate dots
          navigation.style.display = 'flex';
          this.generateCarouselDots(navigation, visibleProducts.length);
          
          // Setup navigation functionality
          this.setupCarouselNavigation(productsContainer, navigation, visibleProducts);
        });
      }
      
      generateCarouselDots(navigation, productCount) {
        const dotsContainer = navigation.querySelector('.dots-nav');
        if (!dotsContainer) return;
        
        // Clear existing dots
        dotsContainer.innerHTML = '';
        
        // Only create dots if we have more than 1 product
        if (productCount <= 1) return;
        
        // Create ALL dots for sliding window effect
        for (let i = 0; i < productCount; i++) {
          const dot = document.createElement('button');
          dot.type = 'button';
          const isActive = i === 0;
          dot.className = `dots-nav__item tap-area ${isActive ? 'active' : ''}`;
          dot.setAttribute('aria-current', isActive ? 'true' : 'false');
          dot.setAttribute('data-index', i);
          
          const span = document.createElement('span');
          span.className = 'visually-hidden';
          span.textContent = `Go to slide ${i + 1}`;
          dot.appendChild(span);
          
          dotsContainer.appendChild(dot);
        }
      }
      
      setupCarouselNavigation(container, navigation, products) {
        const dotsContainer = navigation.querySelector('.dots-nav');
        const dots = navigation.querySelectorAll('.dots-nav__item');
        const prevBtn = navigation.querySelector('.product__media-prev-next:first-child');
        const nextBtn = navigation.querySelector('.product__media-prev-next:last-child');
        
        let currentIndex = 0;
        let isScrolling = false;
        
        // Initialize navigation state
        this.updateCarouselNav(dots, prevBtn, nextBtn, currentIndex, products.length, products, isScrolling, dotsContainer);
        
        // Intersection Observer to detect which product is in view
        const observer = new IntersectionObserver((entries) => {
          // Don't update index if we're programmatically scrolling
          if (isScrolling) return;
          
          // Find the most visible product (highest intersection ratio)
          let mostVisibleIndex = -1;
          let highestRatio = 0;
          
          entries.forEach(entry => {
            if (entry.isIntersecting && entry.intersectionRatio > highestRatio) {
              const index = products.indexOf(entry.target);
              if (index !== -1) {
                highestRatio = entry.intersectionRatio;
                mostVisibleIndex = index;
              }
            }
          });
          
          // Only update if we found a product with significant visibility and it's different from current
          if (mostVisibleIndex !== -1 && mostVisibleIndex !== currentIndex && highestRatio > 0.7) {
            currentIndex = mostVisibleIndex;
            this.updateCarouselNav(dots, prevBtn, nextBtn, currentIndex, products.length, products, isScrolling, dotsContainer);
          }
        }, {
          threshold: [0.1, 0.3, 0.5, 0.7, 0.9],
          root: container
        });
        
        // Observe all products
        products.forEach(product => observer.observe(product));
        
        // Arrow click handlers
        prevBtn.addEventListener('click', () => {
          if (currentIndex > 0 && !isScrolling) {
            isScrolling = true;
            const targetProduct = products[currentIndex - 1];
            targetProduct.scrollIntoView({ 
              behavior: 'smooth', 
              inline: 'center', 
              block: 'nearest' 
            });
            
            // Reset scrolling flag after animation completes
              setTimeout(() => {
                isScrolling = false;
                currentIndex = Math.max(0, currentIndex - 1);
                this.updateCarouselNav(dots, prevBtn, nextBtn, currentIndex, products.length, products, isScrolling, dotsContainer);
              }, 300);
          }
        });
        
        nextBtn.addEventListener('click', () => {
          if (currentIndex < products.length - 1 && !isScrolling) {
            isScrolling = true;
            const targetProduct = products[currentIndex + 1];
            targetProduct.scrollIntoView({ 
              behavior: 'smooth', 
              inline: 'center', 
              block: 'nearest' 
            });
            
            // Reset scrolling flag after animation completes
            setTimeout(() => {
              isScrolling = false;
              currentIndex = Math.min(products.length - 1, currentIndex + 1);
              this.updateCarouselNav(dots, prevBtn, nextBtn, currentIndex, products.length, products, isScrolling, dotsContainer);
            }, 300);
          }
        });
        
        // Dot click handlers
        dots.forEach((dot, index) => {
          dot.addEventListener('click', () => {
            if (!isScrolling && index !== currentIndex) {
              isScrolling = true;
              const targetProduct = products[index];
              targetProduct.scrollIntoView({ 
                behavior: 'smooth', 
                inline: 'center', 
                block: 'nearest' 
              });
              
              // Reset scrolling flag after animation completes
              setTimeout(() => {
                isScrolling = false;
                currentIndex = index;
                this.updateCarouselNav(dots, prevBtn, nextBtn, currentIndex, products.length, products, isScrolling, dotsContainer);
              }, 300);
            }
          });
        });
        
        // Initial state
        this.updateCarouselNav(dots, prevBtn, nextBtn, currentIndex, products.length, products, isScrolling, dotsContainer);
        
        // Store observer for cleanup
        container._carouselObserver = observer;
      }
      
      updateCarouselNav(dots, prevBtn, nextBtn, currentIndex, totalProducts, products, isScrolling, dotsContainer) {
        // Update active state on all dots
        dots.forEach((dot, i) => {
          const isActive = i === currentIndex;
          dot.classList.toggle('active', isActive);
          dot.setAttribute('aria-current', isActive ? 'true' : 'false');
        });
        
        // Update arrow button states
        prevBtn.disabled = currentIndex === 0;
        nextBtn.disabled = currentIndex === totalProducts - 1;
      }
      
      reinitializeMobileCarousels() {
        // Clean up existing observers
        const existingNavs = this.element.querySelectorAll('.bundle-builder__carousel-nav');
        existingNavs.forEach(nav => {
          const container = nav.previousElementSibling;
          if (container && container._carouselObserver) {
            container._carouselObserver.disconnect();
            delete container._carouselObserver;
          }
          // Hide navigation temporarily
          nav.style.display = 'none';
        });
        
        // Reinitialize if in carousel mode
        const mobileLayout = this.element.dataset.mobileLayout || 'grid';
        if (mobileLayout === 'carousel' && window.innerWidth <= 999) {
          setTimeout(() => {
            this.initCarousel();
          }, 100); // Small delay to ensure DOM is updated
        }
      }
      
      destroy() {
        // Clean up carousel observers
        this.element.querySelectorAll('.bundle-builder__products-inner').forEach(container => {
          if (container._carouselObserver) {
            container._carouselObserver.disconnect();
            delete container._carouselObserver;
          }
        });
        
        // Remove all event listeners
        if (this.boundClickHandler) {
          this.element.removeEventListener('click', this.boundClickHandler);
        }
        
        // Remove gender filter listeners
        const genderInputs = this.element.querySelectorAll('input[name^="bundle-gender"]');
        genderInputs.forEach(input => {
          input.removeEventListener('change', this.handleGenderFilter);
        });
        
        // Remove group tab listeners
        this.elements.groupTabs.forEach(tab => {
          tab.removeEventListener('change', this.handleGroupTabChange);
        });
        
        // Remove size selection listeners
        if (this.elements.productSizes) {
          this.elements.productSizes.removeEventListener('change', this.handleSizeChange);
        }
        
        // Remove button listeners
        if (this.elements.selectItemBtn) {
          this.elements.selectItemBtn.removeEventListener('click', this.handleSelectItem);
        }
        if (this.elements.addToCartBtn) {
          this.elements.addToCartBtn.removeEventListener('click', this.handleAddToCart);
        }
        if (this.elements.mobileAddToCartBtn) {
          this.elements.mobileAddToCartBtn.removeEventListener('click', this.handleAddToCart);
        }
        
        
        // Clear all timeouts and intervals
        if (this.animationTimeouts) {
          this.animationTimeouts.forEach(timeout => clearTimeout(timeout));
        }
        
        // Clear state
        this.state.selectedProducts.clear();
        this.state.variantCache.clear();
        this.state.isLoading = false;
        
        // Clear DOM references
        this.elements = {};
        this.element = null;
      }
    }
    
    // Global registry for cleanup
    window.bundleBuilders = window.bundleBuilders || new Map();
    
    // Initialize bundle builders when DOM is ready
    document.addEventListener('DOMContentLoaded', () => {
      const bundleBuilders = document.querySelectorAll('.bundle-builder');
      
      bundleBuilders.forEach(element => {
        const builder = new BundleBuilder(element);
        window.bundleBuilders.set(element.dataset.sectionId, builder);
      });
    });
    
    // Theme editor integration for cleanup
    document.addEventListener('shopify:section:unload', (e) => {
      const builder = window.bundleBuilders?.get(e.detail.sectionId);
      if (builder) {
        builder.destroy();
        window.bundleBuilders.delete(e.detail.sectionId);
      }
    });
    
    // Export for potential external use
    window.BundleBuilder = BundleBuilder;
    
  })();
